import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowRight, ArrowUpRight, Shield, Zap, Sparkles, Users, Globe, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CATEGORIES, TESTIMONIALS, FAQ } from "@/data/campaigns";
import { fetchCampaigns, resolveMediaUrl } from "@/lib/api";
import CampaignCard from "@/components/CampaignCard";
import ProgressBar from "@/components/ProgressBar";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import WaitlistCounter from "@/components/WaitlistCounter";

const Home = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [featuredList, setFeaturedList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchCampaigns({ sort: "raised", limit: 6 }),
      fetchCampaigns({ featured: true, limit: 3 }).catch(() => []),
    ]).then(([d, f]) => {
      setCampaigns(d);
      setFeaturedList(f);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const featured = featuredList[0] || campaigns[0];

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-cream-100">
        <div className="absolute -top-32 -right-24 h-[520px] w-[520px] blob-saffron opacity-70 pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 h-[500px] w-[500px] blob-cream opacity-60 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full border border-saffron-500/30 bg-saffron-500/10 backdrop-blur px-3 py-1.5 text-xs font-semibold text-saffron-700 mb-6" data-testid="hero-beta-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-saffron-600 animate-pulse" />
                Now in early access · Launching soon
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-navy-800 leading-[1.05]">
                Launch ideas.<br />
                <span className="text-saffron-accent">Build the future.</span>
              </h1>
              <p className="mt-6 text-lg text-navy-500 max-w-xl leading-[1.7]">
                Dwinov is where creators, founders, and changemakers raise funding through a community that shows up — from first pledge to final ship.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Button asChild size="lg" className="btn-saffron rounded-2xl h-12 px-6 font-semibold" data-testid="hero-start-btn">
                  <a href="#early-access">Join Early Access <ArrowRight className="ml-2 h-4 w-4" /></a>
                </Button>
                <Button asChild size="lg" variant="outline" className="btn-navy-outline rounded-2xl h-12 px-6 font-semibold border-2" data-testid="hero-explore-btn">
                  <Link to="/explore">Explore campaigns</Link>
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-navy-500">
                <span className="inline-flex items-center gap-1.5"><Shield className="h-4 w-4 text-saffron-600" /> Verified creators</span>
                <span className="inline-flex items-center gap-1.5"><Zap className="h-4 w-4 text-forest-500" /> All-or-nothing funding</span>
              </div>
            </div>

            <div className="lg:col-span-5 relative">
              {featured && (
                <div className="relative bg-white rounded-[24px] border border-warm-200 overflow-hidden shadow-warm-lg hover:-translate-y-1 transition-transform duration-500">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={featured.cover} alt={featured.title} className="h-full w-full object-cover" />
                  </div>
                  <div className="p-6">
                    <span className="text-xs font-semibold text-saffron-700 bg-saffron-50 rounded-full px-2.5 py-1">{featured.category}</span>
                    <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-navy-800">{featured.title}</h3>
                    <div className="mt-2 flex items-center gap-2 text-sm text-navy-500">
                      {featured.creator?.avatar_url && <img src={resolveMediaUrl(featured.creator.avatar_url)} alt={featured.creator.name} className="h-5 w-5 rounded-full" />}
                      <span>{featured.creator?.name}</span>
                    </div>
                    <div className="mt-5">
                      <ProgressBar value={Math.min((featured.raised / featured.goal) * 100, 100)} />
                      <div className="mt-3 flex justify-between text-sm">
                        <span className="font-bold text-navy-800">${(featured.raised / 1000).toFixed(0)}K raised</span>
                        <span className="text-saffron-600 font-bold">{Math.round((featured.raised / featured.goal) * 100)}% funded</span>
                      </div>
                    </div>
                    <Link to={`/campaign/${featured.id}`} className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-saffron-600 hover:gap-2 transition-all">
                      View campaign <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* EARLY ACCESS BANNER */}
      <section id="early-access" className="relative overflow-hidden border-y border-warm-200 bg-navy-800 text-white scroll-mt-20">
        <div className="absolute -top-32 -right-24 h-[420px] w-[420px] blob-saffron opacity-40 pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 h-[420px] w-[420px] blob-cream opacity-25 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-saffron-500/40 bg-saffron-500/15 px-3 py-1 text-xs font-semibold text-saffron-300">
                <Sparkles className="h-3.5 w-3.5" /> Launching Soon
              </span>
              <h2 className="mt-4 font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-white">
                Dwinov is in <span className="text-saffron-accent">active development.</span>
              </h2>
              <p className="mt-5 text-lg text-navy-200 max-w-xl leading-[1.7]">
                Early access begins shortly. Register your interest and we'll reach out with an invite before we open publicly — creators first, then backers.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-navy-200">
                <li className="flex items-start gap-3"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-saffron-500 flex-shrink-0" /> Priority onboarding for the first 500 creators</li>
                <li className="flex items-start gap-3"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-saffron-500 flex-shrink-0" /> Discounted platform fees during beta</li>
                <li className="flex items-start gap-3"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-saffron-500 flex-shrink-0" /> Direct feedback line to the founding team</li>
              </ul>

              <div className="mt-10 pt-8 border-t border-white/10">
                <div className="flex items-center gap-2 mb-4">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-saffron-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-saffron-500" />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-saffron-300">Live waitlist</span>
                </div>
                <WaitlistCounter tone="dark" />
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-[24px] bg-cream-100 text-navy-800 p-6 sm:p-8 shadow-warm-lg">
                <div className="flex items-center gap-2 mb-4">
                  <div className="h-2 w-2 rounded-full bg-saffron-500 animate-pulse" />
                  <span className="text-xs font-semibold uppercase tracking-[0.2em] text-saffron-700">Reserve your spot</span>
                </div>
                <h3 className="font-display text-2xl font-bold tracking-tight text-navy-800">Register &amp; Join early access</h3>
                <p className="mt-1.5 text-sm text-navy-500 leading-[1.6]">Tell us who you are — we'll send an invite the moment your category opens.</p>
                <div className="mt-5">
                  <EarlyAccessForm source="home" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="max-w-2xl">
          <span className="text-sm font-medium text-saffron-600">How Dwinov works</span>
          <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">Three steps from idea to shipped.</h2>
          <p className="mt-4 text-navy-600 text-lg">A protocol built on trust. Backers only pay if the goal is met. Creators only ship when they're ready.</p>
        </div>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Build your page", d: "Tell your story. Set your funding goal. Design your reward tiers with our guided editor." },
            { n: "02", t: "Rally your community", d: "Reach the earliest community of Dwinov backers and get discovered through curated early-access collections." },
            { n: "03", t: "Ship what you promised", d: "Funds unlock only when your goal is met. Ship updates and rewards on your timeline." }
          ].map((step) => (
            <div key={step.n} className="group relative rounded-2xl border border-warm-200 bg-white p-8 hover:border-saffron-200 hover:shadow-lg hover:shadow-saffron-500/5 hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-300">
              <div className="font-display text-6xl font-semibold text-slate-100 group-hover:text-saffron-100 transition-colors">{step.n}</div>
              <h3 className="mt-2 font-display text-xl font-semibold tracking-tight text-navy-800">{step.t}</h3>
              <p className="mt-3 text-navy-600 leading-relaxed">{step.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED CAMPAIGNS */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24 lg:pb-32">
        {featuredList.length > 0 && (
          <div className="mb-16">
            <div className="flex items-end justify-between mb-8">
              <div>
                <span className="text-sm font-medium text-saffron-600">Editor's picks</span>
                <h2 className="mt-2 font-display text-3xl md:text-4xl font-semibold tracking-tight text-navy-800">Featured campaigns.</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="featured-section">
              {featuredList.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
            </div>
          </div>
        )}

        <div className="flex items-end justify-between mb-10">
          <div>
            <span className="text-sm font-medium text-saffron-600">Trending now</span>
            <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">Projects worth watching.</h2>
          </div>
          <Link to="/explore" className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:text-saffron-600 transition-colors" data-testid="featured-see-all">
            See all <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl border border-warm-200 bg-white overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-slate-100 rounded animate-pulse" />
                  <div className="h-4 w-2/3 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {campaigns.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
          </div>
        )}
      </section>

      {/* CATEGORIES */}
      <section className="bg-cream-100/70 border-y border-warm-200">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24">
          <div className="flex items-end justify-between mb-10">
            <div>
              <span className="text-sm font-medium text-saffron-600">Categories</span>
              <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">Fund what you love.</h2>
            </div>
            <Link to="/categories" className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 hover:text-saffron-600 transition-colors">
              All categories <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              return (
                <Link key={c.id} to={`/explore?category=${c.id}`} data-testid={`category-${c.id}`}
                  className="group relative rounded-[20px] border border-warm-200 bg-white p-5 shadow-warm hover:shadow-warm-lg hover:border-saffron-200 hover:-translate-y-0.5 transition-[transform,box-shadow,border-color] duration-300">
                  <div className={`inline-grid place-items-center h-11 w-11 rounded-xl bg-gradient-to-br ${c.color} text-white shadow-sm`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="mt-4 font-display font-bold text-navy-800 group-hover:text-saffron-600 transition-colors">{c.name}</div>
                  <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-saffron-700" data-testid={`category-badge-home-${c.id}`}>
                    <span aria-hidden>{c.badge.emoji}</span>
                    <span>{c.badge.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY DWINOV */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5">
            <span className="text-sm font-medium text-saffron-600">Why Dwinov</span>
            <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">Built for people who ship.</h2>
            <p className="mt-4 text-navy-600 text-lg leading-relaxed">
              We designed Dwinov for the moments that matter — the first pledge, the goal met, the final ship. No hidden fees, no bloated dashboards.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { i: Shield, t: "Trust by design", d: "Every creator is manually verified. Every payout is logged on-chain optional." },
              { i: Zap, t: "Fast & fair fees", d: "Flat 5% platform fee. No surprises. Free if you don't hit your goal." },
              { i: Users, t: "A real community", d: "Founded by backers, not investors. We're building Dwinov alongside our earliest members — every voice counts." },
              { i: Globe, t: "Global by default", d: "Ship rewards to 120+ countries with unified logistics tooling." }
            ].map((f, idx) => (
              <div key={idx} className="rounded-2xl border border-warm-200 bg-white p-6 hover:border-saffron-200 hover:shadow-md transition-[box-shadow,border-color] duration-300">
                <div className="inline-grid place-items-center h-10 w-10 rounded-xl bg-saffron-50 text-saffron-600 mb-4">
                  <f.i className="h-5 w-5" />
                </div>
                <div className="font-display font-semibold text-navy-800">{f.t}</div>
                <p className="mt-1 text-sm text-navy-600 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-navy-800 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <div className="max-w-2xl">
            <span className="text-sm font-medium text-saffron-400">Loved by makers</span>
            <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight">Testimonials from the community.</h2>
          </div>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t) => (
              <div key={t.id} className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur hover:bg-white/[0.08] transition-colors">
                <div className="flex items-center gap-1 text-amber-400 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400" />)}
                </div>
                <p className="text-slate-200 leading-relaxed">"{t.quote}"</p>
                <div className="mt-6 flex items-center gap-3">
                  <img src={t.avatar} alt={t.name} className="h-10 w-10 rounded-full object-cover" />
                  <div>
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-slate-400">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-sm font-medium text-saffron-600">FAQ</span>
          <h2 className="mt-2 font-display text-4xl md:text-5xl font-semibold tracking-tight text-navy-800">Frequently asked.</h2>
        </div>
        <Accordion type="single" collapsible className="mt-12" data-testid="home-faq">
          {FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-warm-200">
              <AccordionTrigger className="text-left font-display font-semibold text-navy-800 hover:text-saffron-600 hover:no-underline py-6">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-navy-600 leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-24">
        <div className="relative overflow-hidden rounded-[24px] bg-navy-800 p-12 lg:p-20 text-white">
          <div className="absolute -top-32 -right-24 h-[420px] w-[420px] blob-saffron opacity-40 pointer-events-none" />
          <div className="relative max-w-2xl">
            <h2 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-[1.05]">Your idea deserves <span className="text-saffron-accent">an audience.</span></h2>
            <p className="mt-4 text-lg text-navy-200 leading-[1.7]">Publish your campaign in under 20 minutes. Get feedback from real backers before you launch.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-saffron rounded-2xl h-12 px-6 font-semibold" data-testid="cta-start-btn">
                <Link to="/start">Start your campaign <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-2xl bg-transparent border-2 border-white/40 text-white hover:bg-white/10 hover:text-white h-12 px-6 font-semibold">
                <Link to="/pricing">See pricing</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
